import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Upload, 
  Download, 
  FileSpreadsheet, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Users,
  ClipboardList,
  ArrowRight
} from 'lucide-react';

import { FixPatientCodesCard } from '../components/FixPatientCodesCard';
import { MergeDuplicatesCard } from '../components/MergeDuplicatesCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BulkImportPage = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('patients');
  
  // Patient import states
  const [patientFile, setPatientFile] = useState(null);
  const [importingPatients, setImportingPatients] = useState(false);
  const [patientResult, setPatientResult] = useState(null);
  const patientFileRef = useRef(null);
  
  // Evaluation import states
  const [evalFile, setEvalFile] = useState(null);
  const [importingEvals, setImportingEvals] = useState(false);
  const [evalResult, setEvalResult] = useState(null);
  const evalFileRef = useRef(null);

  // Download patient template
  const handleDownloadPatientTemplate = async () => {
    try {
      const response = await axios.get(`${API}/patients/bulk/template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_importacion_pacientes.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Plantilla de pacientes descargada');
    } catch (error) {
      toast.error('Error al descargar la plantilla');
    }
  };

  // Download evaluation template
  const handleDownloadEvalTemplate = async () => {
    try {
      const response = await axios.get(`${API}/evaluations/bulk/template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_importacion_evaluaciones.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Plantilla de evaluaciones descargada');
    } catch (error) {
      toast.error('Error al descargar la plantilla');
    }
  };

  // Handle patient file selection
  const handlePatientFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error('Por favor seleccione un archivo Excel (.xlsx o .xls)');
        return;
      }
      setPatientFile(file);
      setPatientResult(null);
    }
  };

  // Handle evaluation file selection
  const handleEvalFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error('Por favor seleccione un archivo Excel (.xlsx o .xls)');
        return;
      }
      setEvalFile(file);
      setEvalResult(null);
    }
  };

  // Import patients
  const handleImportPatients = async () => {
    if (!patientFile) {
      toast.error('Por favor seleccione un archivo');
      return;
    }

    setImportingPatients(true);
    setPatientResult(null);

    try {
      const formData = new FormData();
      formData.append('file', patientFile);

      const response = await axios.post(`${API}/patients/bulk/import`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setPatientResult(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`${response.data.imported} pacientes importados exitosamente`);
      }
      
      if (response.data.errors > 0) {
        toast.warning(`${response.data.errors} filas con errores`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar pacientes');
      setPatientResult(null);
    } finally {
      setImportingPatients(false);
    }
  };

  // Import evaluations
  const handleImportEvals = async () => {
    if (!evalFile) {
      toast.error('Por favor seleccione un archivo');
      return;
    }

    setImportingEvals(true);
    setEvalResult(null);

    try {
      const formData = new FormData();
      formData.append('file', evalFile);

      const response = await axios.post(`${API}/evaluations/bulk/import`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setEvalResult(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`${response.data.imported} evaluaciones importadas exitosamente`);
      }
      
      if (response.data.errors > 0) {
        toast.warning(`${response.data.errors} filas con errores`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar evaluaciones');
      setEvalResult(null);
    } finally {
      setImportingEvals(false);
    }
  };

  // Reset patient import
  const resetPatientImport = () => {
    setPatientFile(null);
    setPatientResult(null);
    if (patientFileRef.current) {
      patientFileRef.current.value = '';
    }
  };

  // Reset evaluation import
  const resetEvalImport = () => {
    setEvalFile(null);
    setEvalResult(null);
    if (evalFileRef.current) {
      evalFileRef.current.value = '';
    }
  };

  const ImportResultDisplay = ({ result }) => (
    <div className={`rounded-lg p-4 ${
      result.errors === 0 ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
    }`}>
      <div className="flex items-center gap-2 mb-3">
        {result.errors === 0 ? (
          <CheckCircle className="h-5 w-5 text-green-600" />
        ) : (
          <AlertTriangle className="h-5 w-5 text-yellow-600" />
        )}
        <span className="font-medium text-lg">Resultado de la importación</span>
      </div>
      <div className={`grid gap-3 text-sm ${result.skipped_existing !== undefined ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-3'}`}>
        <div className="text-center p-3 bg-white rounded-lg shadow-sm">
          <p className="text-3xl font-bold text-slate-700">{result.total_rows}</p>
          <p className="text-slate-500">Total filas</p>
        </div>
        <div className="text-center p-3 bg-white rounded-lg shadow-sm">
          <p className="text-3xl font-bold text-green-600">{result.imported}</p>
          <p className="text-slate-500">Importados</p>
        </div>
        {result.skipped_existing !== undefined && (
          <div className="text-center p-3 bg-white rounded-lg shadow-sm">
            <p className="text-3xl font-bold text-slate-500" data-testid="import-skipped-existing">{result.skipped_existing}</p>
            <p className="text-slate-500">Ya existían (omitidas)</p>
          </div>
        )}
        <div className="text-center p-3 bg-white rounded-lg shadow-sm">
          <p className="text-3xl font-bold text-red-600">{result.errors}</p>
          <p className="text-slate-500">Errores</p>
        </div>
      </div>
      
      {result.error_details && result.error_details.length > 0 && (
        <div className="mt-4 max-h-48 overflow-y-auto bg-white rounded-lg p-3">
          <p className="text-sm font-medium text-slate-600 mb-2">Detalles de errores:</p>
          {result.error_details.map((err, idx) => (
            <div key={idx} className="text-sm text-red-600 flex gap-2 py-1 border-b last:border-b-0">
              <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span><strong>Fila {err.row}:</strong> {err.error}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="bulk-import-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-[#0B3B71]">Importación Masiva</h1>
        <p className="text-slate-500 mt-1">Importa pacientes y evaluaciones desde archivos Excel</p>
      </div>

      {/* Process Flow */}
      <Card className="bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold">1</div>
              <span className="font-medium text-slate-700">Importar Pacientes</span>
            </div>
            <ArrowRight className="h-5 w-5 text-slate-400 hidden sm:block" />
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold">2</div>
              <span className="font-medium text-slate-700">Importar Evaluaciones</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="patients" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Pacientes
          </TabsTrigger>
          <TabsTrigger value="evaluations" className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4" />
            Evaluaciones
          </TabsTrigger>
        </TabsList>

        {/* Patients Tab */}
        <TabsContent value="patients">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#0B3B71]">
                <Users className="h-5 w-5" />
                Paso 1: Importar Pacientes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Instructions */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-medium text-green-800 mb-2">Instrucciones:</h4>
                <ol className="text-sm text-green-700 space-y-1 list-decimal list-inside">
                  <li>Descargue la plantilla de Excel</li>
                  <li>Complete los datos de los pacientes (incluya el Código MedicPro si tiene)</li>
                  <li>Suba el archivo completado</li>
                </ol>
              </div>

              {/* Download Template */}
              <Button 
                variant="outline" 
                onClick={handleDownloadPatientTemplate}
                className="w-full border-green-500 text-green-700 hover:bg-green-50"
                data-testid="download-patient-template-button"
              >
                <Download className="h-4 w-4 mr-2" />
                Descargar Plantilla de Pacientes
              </Button>

              {/* File Upload */}
              <div 
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  patientFile ? 'border-green-400 bg-green-50' : 'border-slate-300 hover:border-green-500'
                }`}
              >
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handlePatientFileSelect}
                  ref={patientFileRef}
                  className="hidden"
                  id="patient-file-input"
                  data-testid="patient-file-input"
                />
                <label htmlFor="patient-file-input" className="cursor-pointer">
                  {patientFile ? (
                    <div className="flex flex-col items-center gap-2">
                      <CheckCircle className="h-12 w-12 text-green-500" />
                      <span className="font-medium text-green-700">{patientFile.name}</span>
                      <span className="text-sm text-slate-500">Clic para cambiar archivo</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <FileSpreadsheet className="h-12 w-12 text-slate-400" />
                      <span className="font-medium text-slate-600">Seleccionar archivo Excel</span>
                      <span className="text-sm text-slate-400">o arrastre y suelte aquí</span>
                    </div>
                  )}
                </label>
              </div>

              {/* Results */}
              {patientResult && <ImportResultDisplay result={patientResult} />}

              {/* Actions */}
              <div className="flex gap-2">
                {patientResult ? (
                  <Button onClick={resetPatientImport} className="flex-1 bg-slate-600 hover:bg-slate-700">
                    Nueva Importación
                  </Button>
                ) : (
                  <>
                    <Button 
                      variant="outline" 
                      onClick={resetPatientImport}
                      className="flex-1"
                      disabled={!patientFile}
                    >
                      Cancelar
                    </Button>
                    <Button 
                      onClick={handleImportPatients}
                      disabled={!patientFile || importingPatients}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      data-testid="import-patients-button"
                    >
                      {importingPatients ? (
                        <>
                          <span className="animate-spin mr-2">⏳</span>
                          Importando...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Importar Pacientes
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
          <MergeDuplicatesCard token={token} />
          <FixPatientCodesCard token={token} />
        </TabsContent>

        {/* Evaluations Tab */}
        <TabsContent value="evaluations">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#0B3B71]">
                <ClipboardList className="h-5 w-5" />
                Paso 2: Importar Evaluaciones
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Warning */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-medium text-amber-800 mb-1 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Importante
                </h4>
                <p className="text-sm text-amber-700">
                  Las evaluaciones se vinculan por <strong>Nombre del Paciente</strong> (sin importar mayúsculas ni tildes).
                  El código solo se usa si hay dos pacientes con el mismo nombre.
                </p>
              </div>

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-2">Instrucciones:</h4>
                <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                  <li>Descargue la plantilla de evaluaciones</li>
                  <li>Escriba el <strong>Nombre del Paciente</strong> tal como está registrado</li>
                  <li>Complete los datos del examen oftalmológico</li>
                  <li>Suba el archivo completado</li>
                </ol>
              </div>

              {/* Download Template */}
              <Button 
                variant="outline" 
                onClick={handleDownloadEvalTemplate}
                className="w-full border-blue-500 text-blue-700 hover:bg-blue-50"
                data-testid="download-eval-template-button"
              >
                <Download className="h-4 w-4 mr-2" />
                Descargar Plantilla de Evaluaciones
              </Button>

              {/* File Upload */}
              <div 
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  evalFile ? 'border-blue-400 bg-blue-50' : 'border-slate-300 hover:border-blue-500'
                }`}
              >
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleEvalFileSelect}
                  ref={evalFileRef}
                  className="hidden"
                  id="eval-file-input"
                  data-testid="eval-file-input"
                />
                <label htmlFor="eval-file-input" className="cursor-pointer">
                  {evalFile ? (
                    <div className="flex flex-col items-center gap-2">
                      <CheckCircle className="h-12 w-12 text-blue-500" />
                      <span className="font-medium text-blue-700">{evalFile.name}</span>
                      <span className="text-sm text-slate-500">Clic para cambiar archivo</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <FileSpreadsheet className="h-12 w-12 text-slate-400" />
                      <span className="font-medium text-slate-600">Seleccionar archivo Excel</span>
                      <span className="text-sm text-slate-400">o arrastre y suelte aquí</span>
                    </div>
                  )}
                </label>
              </div>

              {/* Results */}
              {evalResult && <ImportResultDisplay result={evalResult} />}

              {/* Actions */}
              <div className="flex gap-2">
                {evalResult ? (
                  <Button onClick={resetEvalImport} className="flex-1 bg-slate-600 hover:bg-slate-700">
                    Nueva Importación
                  </Button>
                ) : (
                  <>
                    <Button 
                      variant="outline" 
                      onClick={resetEvalImport}
                      className="flex-1"
                      disabled={!evalFile}
                    >
                      Cancelar
                    </Button>
                    <Button 
                      onClick={handleImportEvals}
                      disabled={!evalFile || importingEvals}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                      data-testid="import-evals-button"
                    >
                      {importingEvals ? (
                        <>
                          <span className="animate-spin mr-2">⏳</span>
                          Importando...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Importar Evaluaciones
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BulkImportPage;
